'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { transferRequestApi } from '../api/transferRequestApi'
import type { TransferDetail, TransferRequest } from '../types/transferRequest'

const invalidateTransferQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['transfer-requests'] })
  queryClient.invalidateQueries({ queryKey: ['active-transfer-requests'] })
}

const invalidateTransferItemQuery = (queryClient: ReturnType<typeof useQueryClient>, itemId?: string) => {
  if (itemId) queryClient.invalidateQueries({ queryKey: ['item', itemId] })
}

export const useGetTransferRequests = (params?: { status?: string; departmentId?: string }, enabled = true) => {
  return useQuery<TransferRequest[]>({
    queryKey: ['transfer-requests', params],
    queryFn: () => transferRequestApi.getTransferRequests(params),
    enabled,
  })
}

export const useGetActiveTransferRequests = (params?: { status?: string; departmentId?: string }, enabled = true) => {
  return useQuery<TransferRequest[]>({
    queryKey: ['active-transfer-requests', params],
    queryFn: () => transferRequestApi.getActiveTransferRequests(params),
    enabled,
  })
}

export const useGetTransferRequest = (id?: string | null, enabled = true) => {
  return useQuery<TransferDetail>({
    queryKey: ['transfer-request', id],
    queryFn: () => transferRequestApi.getTransferRequest(id!),
    enabled: enabled && !!id,
  })
}

export const useCreateTransferRequest = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: transferRequestApi.createTransferRequest,
    onSuccess: (_data, variables) => {
      invalidateTransferQueries(queryClient)
      invalidateTransferItemQuery(queryClient, variables.itemId)
    },
  })
}

export const useCreateBorrowTransferRequest = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: transferRequestApi.createBorrowTransferRequest,
    onSuccess: (_data, variables) => {
      invalidateTransferQueries(queryClient)
      invalidateTransferItemQuery(queryClient, variables.itemId)
    },
  })
}

export const useCreateReturnTransferRequest = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: transferRequestApi.createReturnTransferRequest,
    onSuccess: (_data, variables) => {
      invalidateTransferQueries(queryClient)
      invalidateTransferItemQuery(queryClient, variables.itemId)
    },
  })
}

export const useApproveTransferRequest = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload?: any }) =>
      transferRequestApi.approveTransferRequest(id, payload),
    onSuccess: (_data, variables) => {
      invalidateTransferQueries(queryClient)
    },
  })
}

export const useRejectTransferRequest = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload?: any }) =>
      transferRequestApi.rejectTransferRequest(id, payload),
    onSuccess: (_data, variables) => {
      invalidateTransferQueries(queryClient)
    },
  })
}

export const useScheduleTransferRequest = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload?: any }) =>
      transferRequestApi.scheduleTransferRequest(id, payload),
    onSuccess: (_data, variables) => {
      invalidateTransferQueries(queryClient)
    },
  })
}

export const useDeliverTransferRequest = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload?: any }) =>
      transferRequestApi.deliverTransferRequest(id, payload),
    onSuccess: (_data, variables) => {
      invalidateTransferQueries(queryClient)
    },
  })
}

export const useMarkTransferPickupReady = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload?: any }) =>
      transferRequestApi.markTransferPickupReady(id, payload),
    onSuccess: (_data, variables) => {
      invalidateTransferQueries(queryClient)
    },
  })
}

export const useReceiveTransferRequest = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; itemId?: string; payload?: any }) =>
      transferRequestApi.receiveTransferRequest(id, payload),
    onSuccess: (data, variables) => {
      invalidateTransferQueries(queryClient)
      queryClient.invalidateQueries({ queryKey: ['transfer-request', variables.id] })
      const itemId = variables.itemId ?? data?.itemId
      if (itemId) {
        queryClient.invalidateQueries({ queryKey: ['item', itemId] })
        queryClient.invalidateQueries({ queryKey: ['item-transfer', itemId] })
      }
    },
  })
}

export const useGetTransferRequestLogs = (id?: string | null, enabled = true) => {
  return useQuery({
    queryKey: ['transfer-request-logs', id],
    queryFn: () => transferRequestApi.getTransferRequestLogs(id!),
    enabled: enabled && !!id,
  })
}
